import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, GraduationCap, ChevronDown, ChevronUp, List, X, PlusCircle, Copy, Upload, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCourses, addCourse, updateCourse, deleteCourse } from '@/lib/firebase/queries/courses';
import { getInstructors, type Instructor } from '@/lib/firebase/queries/users';
import type { Course, CourseModule, QuizQuestion } from '@/components/education/types';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { v4 as uuidv4 } from 'uuid';
import { appendModule, duplicateModuleAt, moveModule, removeModuleAt, replaceModule } from '@/lib/courses/courseModules';
import { Progress } from '@/components/ui/progress';
import { storage } from '@/lib/firebase/config';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

interface CourseManagementProps {
  contentMode?: 'general' | 'organization';
  organizationId?: string;
}

export function CourseManagement({ contentMode = 'general', organizationId }: CourseManagementProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [isManagingModules, setIsManagingModules] = useState(false);
  const [newModule, setNewModule] = useState<Partial<CourseModule>>({
    id: uuidv4(),
    title: '',
    duration: '',
    type: 'video',
    completed: false
  });
  const [newCourse, setNewCourse] = useState<Partial<Course>>({
    title: '',
    description: '',
    category: '',
    totalLessons: 0,
    certificate: false,
    modules: [],
    instructorId: user?.uid || '',
    instructorName: '',
    contentMode: contentMode,
    isOrganizationOnly: contentMode === 'organization'
  });
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null);
  const [moduleForm, setModuleForm] = useState<Partial<CourseModule>>({
    id: uuidv4(),
    title: '',
    duration: '',
    type: 'video'
  });
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [interactiveTexts, setInteractiveTexts] = useState<string[]>(['']);
  
  // Quiz state
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([{
    id: uuidv4(),
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: ''
  }]);
  const [passingScore, setPassingScore] = useState(70);
  const [allowRetry, setAllowRetry] = useState(true);

  // Video upload state
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string>('');
  const videoInputRef = useRef<HTMLInputElement>(null);

  // Video upload handler
  const handleVideoUpload = async (file: File, courseId: string, moduleId: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split('.').pop();
      const storagePath = `course-videos/${courseId}/${moduleId}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);
      
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setVideoUploadProgress(progress);
        },
        (error) => {
          console.error('Video upload error:', error);
          setIsUploadingVideo(false);
          reject(error);
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadedVideoUrl(downloadUrl);
            setIsUploadingVideo(false);
            resolve(downloadUrl);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  const handleVideoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error(t('dashboard.instructor.dashboard.errors.invalidVideoType'));
        return;
      }
      // Validate file size (500MB max)
      if (file.size > 500 * 1024 * 1024) {
        toast.error(t('dashboard.instructor.dashboard.errors.videoTooLarge'));
        return;
      }
      setVideoFile(file);
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setUploadedVideoUrl(previewUrl);
    }
  };

  const clearVideoSelection = () => {
    setVideoFile(null);
    setUploadedVideoUrl('');
    setVideoUploadProgress(0);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (user?.uid) {
      loadCourses();
      loadInstructors();
    }
  }, [user?.uid, contentMode, organizationId]);

  // Update course contentMode when it changes
  useEffect(() => {
    setNewCourse(prev => ({
      ...prev,
      contentMode: contentMode,
      isOrganizationOnly: contentMode === 'organization',
      organizationId: contentMode === 'organization' ? organizationId : undefined
    }));
  }, [contentMode, organizationId]);

  const loadInstructors = async () => {
    try {
      const allInstructors = await getInstructors();
      setInstructors(allInstructors);
      const currentInstructor = allInstructors.find(i => i.id === user?.uid);
      if (currentInstructor) {
        setNewCourse(prev => ({
          ...prev,
          instructorName: currentInstructor.name
        }));
      }
    } catch (error) {
      console.error('Error loading instructors:', error);
      toast.error(t('dashboard.instructor.errors.loadInstructors'));
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const allCourses = await getCourses();
      
      // Filter courses based on instructor and current context
      let filteredCourses = allCourses.filter(course => course.instructorId === user?.uid);
      
      if (contentMode === 'general') {
        // Show only public courses (not organization-specific) for general mode
        filteredCourses = filteredCourses.filter(course => !course.isOrganizationOnly);
      } else {
        // Show only courses for the current organization
        filteredCourses = filteredCourses.filter(course => 
          course.isOrganizationOnly && course.organizationId === organizationId
        );
      }
      
      setCourses(filteredCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error(t('dashboard.instructor.errors.loadCourses'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = async () => {
    try {
      if (!newCourse.title || !newCourse.description || !newCourse.category) {
        toast.error(t('dashboard.instructor.errors.requiredFields'));
        return;
      }

      const selectedInstructor = instructors.find(i => i.id === user?.uid);
      if (!selectedInstructor) {
        toast.error(t('dashboard.instructor.errors.instructorNotFound'));
        return;
      }

      const courseData: Course = {
        id: uuidv4(),
        title: newCourse.title,
        description: newCourse.description,
        category: newCourse.category,
        totalLessons: 0,
        completedLessons: 0,
        progress: 0,
        certificate: newCourse.certificate || false,
        modules: [],
        instructorId: user?.uid || '',
        instructorName: selectedInstructor.name,
        contentMode: contentMode,
        isOrganizationOnly: contentMode === 'organization',
        organizationId: contentMode === 'organization' ? organizationId : undefined
      };

      await addCourse(courseData);
      toast.success(t('dashboard.instructor.success.courseAdded'));
      setIsAddingCourse(false);
      setNewCourse({
        title: '',
        description: '',
        category: '',
        totalLessons: 0,
        certificate: false,
        modules: [],
        instructorId: user?.uid || '',
        instructorName: '',
        contentMode: contentMode,
        isOrganizationOnly: contentMode === 'organization',
        organizationId: contentMode === 'organization' ? organizationId : undefined
      });
      await loadCourses();
    } catch (error) {
      console.error('Error adding course:', error);
      toast.error(t('dashboard.instructor.errors.addCourse'));
    }
  };

  const handleEditCourse = async (course: Course) => {
    try {
      setEditingCourse(course);
      setNewCourse({
        title: course.title,
        description: course.description,
        category: course.category,
        totalLessons: course.totalLessons,
        certificate: course.certificate,
        modules: course.modules,
        instructorId: user?.uid || '',
        instructorName: course.instructorName
      });
      setIsAddingCourse(true);
    } catch (error) {
      console.error('Error preparing course for edit:', error);
      toast.error(t('dashboard.instructor.errors.editCourse'));
    }
  };

  const handleManageModules = (course: Course) => {
    setEditingCourse(course);
    setNewCourse(course);
    setIsManagingModules(true);
  };

  // Save a unit-list edit and keep the saved course as the source for the next
  // edit. Before namat#75 only newCourse was updated, so every later add,
  // delete or move started from the list as it was when the dialog opened.
  const saveCourseModules = async (updatedCourse: Course) => {
    await updateCourse(updatedCourse.id, updatedCourse);
    setEditingCourse(updatedCourse);
    setNewCourse(updatedCourse);
  };

  const handleAddModule = () => {
    setNewModule({
      id: uuidv4(),
      title: '',
      duration: '',
      type: 'video',
      completed: false
    });
    setIsAddingModule(true);
  };

  const handleSaveModule = async () => {
    if (!newModule.title || !newModule.duration) {
      toast.error(t('dashboard.instructor.errors.requiredFields'));
      return;
    }

    // Validate video content if needed
    if (newModule.type === 'video' && !videoFile && !uploadedVideoUrl) {
      toast.error(t('dashboard.instructor.dashboard.errors.videoRequired'));
      return;
    }

    // Validate interactive content if needed
    if (newModule.type === 'interactive') {
      const validTexts = interactiveTexts.filter(text => text.trim() !== '');
      if (validTexts.length === 0) {
        toast.error(t('dashboard.instructor.dashboard.errors.interactiveTextRequired'));
        return;
      }
    }

    // Validate quiz content if needed
    if (newModule.type === 'quiz') {
      const validQuestions = quizQuestions.filter(q => 
        q.question.trim() !== '' && 
        q.options.every(option => option.trim() !== '')
      );
      if (validQuestions.length === 0) {
        toast.error(t('dashboard.instructor.dashboard.errors.quizQuestionsRequired'));
        return;
      }
    }

    if (!editingCourse) {
      toast.error(t('dashboard.instructor.errors.courseNotFound'));
      return;
    }

    try {
      const moduleId = newModule.id || uuidv4();
      
      // Prepare module data with interactive content if applicable
      const moduleData: Partial<CourseModule> = { ...newModule };
      
      // Handle video upload
      if (newModule.type === 'video' && videoFile) {
        setIsUploadingVideo(true);
        toast.info(t('dashboard.instructor.dashboard.uploadingVideo'));
        try {
          const videoUrl = await handleVideoUpload(videoFile, editingCourse.id, moduleId);
          moduleData.videoUrl = videoUrl;
        } catch (error) {
          console.error('Video upload failed:', error);
          toast.error(t('dashboard.instructor.dashboard.errors.videoUploadFailed'));
          setIsUploadingVideo(false);
          return;
        }
      } else if (newModule.type === 'video' && uploadedVideoUrl && !videoFile) {
        // Keep existing video URL if no new file selected
        moduleData.videoUrl = uploadedVideoUrl;
      }
      
      if (newModule.type === 'interactive') {
        const validTexts = interactiveTexts.filter(text => text.trim() !== '');
        moduleData.interactiveContent = {
          texts: validTexts,
          currentIndex: 0
        };
      } else if (newModule.type === 'quiz') {
        const validQuestions = quizQuestions.filter(q => 
          q.question.trim() !== '' && 
          q.options.every(option => option.trim() !== '')
        );
        moduleData.quizContent = {
          questions: validQuestions,
          passingScore,
          allowRetry
        };
      }

      await saveCourseModules(appendModule(editingCourse, {
        ...moduleData,
        id: moduleId,
        completed: false
      } as CourseModule));

      toast.success(t('dashboard.instructor.dashboard.success.moduleAdded'));
      setIsAddingModule(false);
      setNewModule({
        id: uuidv4(),
        title: '',
        duration: '',
        type: 'video',
        completed: false
      });
      setInteractiveTexts(['']);
      setQuizQuestions([{
        id: uuidv4(),
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      }]);
      setPassingScore(70);
      setAllowRetry(true);
      clearVideoSelection();

      await loadCourses();
    } catch (error) {
      console.error('Error saving module:', error);
      toast.error(t('dashboard.instructor.errors.updateCourse'));
    }
  };

  const handleDeleteModule = async (moduleIndex: number) => {
    if (!confirm(t('dashboard.instructor.confirm.deleteModule'))) {
      return;
    }

    try {
      if (editingCourse) {
        await saveCourseModules(removeModuleAt(editingCourse, moduleIndex));
        toast.success(t('dashboard.instructor.success.moduleDeleted'));
        await loadCourses();
      }
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error(t('dashboard.instructor.errors.deleteModule'));
    }
  };

  const handleMoveModuleUp = async (moduleIndex: number) => {
    if (moduleIndex === 0) return; // Can't move first item up

    try {
      if (editingCourse) {
        await saveCourseModules(moveModule(editingCourse, moduleIndex, -1));
        toast.success(t('dashboard.instructor.success.moduleReordered'));
        await loadCourses();
      }
    } catch (error) {
      console.error('Error reordering module:', error);
      toast.error(t('dashboard.instructor.errors.reorderModule'));
    }
  };

  const handleMoveModuleDown = async (moduleIndex: number) => {
    if (moduleIndex === (editingCourse?.modules?.length || 0) - 1) return; // Can't move last item down

    try {
      if (editingCourse) {
        await saveCourseModules(moveModule(editingCourse, moduleIndex, 1));
        toast.success(t('dashboard.instructor.success.moduleReordered'));
        await loadCourses();
      }
    } catch (error) {
      console.error('Error reordering module:', error);
      toast.error(t('dashboard.instructor.errors.reorderModule'));
    }
  };

  const handleDuplicateModule = async (moduleIndex: number) => {
    try {
      if (!editingCourse?.modules[moduleIndex]) return;

      await saveCourseModules(duplicateModuleAt(editingCourse, moduleIndex, uuidv4()));
      await loadCourses();
      
      toast.success(t('dashboard.instructor.dashboard.success.moduleDuplicated'));
    } catch (error) {
      console.error('Error duplicating module:', error);
      toast.error(t('dashboard.instructor.errors.updateCourse'));
    }
  };

  const handleUpdateCourse = async () => {
    console.log('handleUpdateCourse called');
    console.log('newCourse before update:', newCourse);
    try {
      if (!editingCourse || !newCourse.title || !newCourse.description || !newCourse.category) {
        toast.error(t('dashboard.instructor.dashboard.errors.requiredFields'));
        return;
      }

      const selectedInstructor = instructors.find(i => i.id === user?.uid);
      if (!selectedInstructor) {
        toast.error(t('dashboard.instructor.dashboard.errors.instructorNotFound'));
        return;
      }

      const courseData = {
        ...newCourse,
        id: editingCourse.id,
        instructorId: user?.uid,
        instructorName: selectedInstructor.name
      };

      console.log('courseData to update:', courseData);

      await updateCourse(editingCourse.id, courseData);
      toast.success(t('dashboard.instructor.dashboard.success.courseUpdated'));
      setIsAddingCourse(false);
      await loadCourses();
    } catch (error) {
      console.error('Error updating course:', error);
      if (error instanceof Error && error.message.includes('does not exist')) {
        toast.error(t('dashboard.instructor.dashboard.errors.courseNotFound'));
      } else {
        toast.error(t('dashboard.instructor.dashboard.errors.updateCourse'));
      }
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    try {
      if (!confirm(t('dashboard.instructor.confirm.deleteCourse'))) {
        return;
      }

      await deleteCourse(courseId);
      toast.success(t('dashboard.instructor.success.courseDeleted'));
      await loadCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error(t('dashboard.instructor.errors.deleteCourse'));
    }
  };

  const handleSelectModule = (module: CourseModule) => {
    setSelectedModule(module);
    setModuleForm({
      id: module.id,
      title: module.title,
      duration: module.duration,
      type: module.type,
      content: module.content || ''
    });
    
    // Load video URL if module is video
    if (module.type === 'video' && module.videoUrl) {
      setUploadedVideoUrl(module.videoUrl);
      setVideoFile(null); // Clear any pending file upload
    } else {
      clearVideoSelection();
    }
    
    // Load interactive content if module is interactive
    if (module.type === 'interactive' && module.interactiveContent) {
      setInteractiveTexts(module.interactiveContent.texts);
    } else {
      setInteractiveTexts(['']);
    }
    
    // Load quiz content if module is quiz
    if (module.type === 'quiz' && module.quizContent) {
      setQuizQuestions(module.quizContent.questions);
      setPassingScore(module.quizContent.passingScore);
      setAllowRetry(module.quizContent.allowRetry);
    } else {
      setQuizQuestions([{
        id: uuidv4(),
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: ''
      }]);
      setPassingScore(70);
      setAllowRetry(true);
    }
  };

  const handleModuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moduleForm.title || !moduleForm.duration) {
      toast.error(t('dashboard.instructor.errors.requiredFields'));
      return;
    }

    // Validate video content if needed
    if (moduleForm.type === 'video' && !videoFile && !uploadedVideoUrl && !selectedModule?.videoUrl) {
      toast.error(t('dashboard.instructor.dashboard.errors.videoRequired'));
      return;
    }

    // Validate interactive content if needed
    if (moduleForm.type === 'interactive') {
      const validTexts = interactiveTexts.filter(text => text.trim() !== '');
      if (validTexts.length === 0) {
        toast.error(t('dashboard.instructor.dashboard.errors.interactiveTextRequired'));
        return;
      }
    }

    // Validate quiz content if needed
    if (moduleForm.type === 'quiz') {
      const validQuestions = quizQuestions.filter(q => 
        q.question.trim() !== '' && 
        q.options.every(option => option.trim() !== '')
      );
      if (validQuestions.length === 0) {
        toast.error(t('dashboard.instructor.dashboard.errors.quizQuestionsRequired'));
        return;
      }
    }

    if (!editingCourse || !selectedModule) {
      toast.error(t('dashboard.instructor.errors.courseNotFound'));
      return;
    }

    try {
      // Prepare module data with interactive content if applicable
      const moduleData: Partial<CourseModule> = { ...moduleForm };
      
      // Handle video upload
      if (moduleForm.type === 'video' && videoFile) {
        setIsUploadingVideo(true);
        toast.info(t('dashboard.instructor.dashboard.uploadingVideo'));
        try {
          const videoUrl = await handleVideoUpload(videoFile, editingCourse.id, selectedModule.id);
          moduleData.videoUrl = videoUrl;
        } catch (error) {
          console.error('Video upload failed:', error);
          toast.error(t('dashboard.instructor.dashboard.errors.videoUploadFailed'));
          setIsUploadingVideo(false);
          return;
        }
      } else if (moduleForm.type === 'video') {
        // Keep existing video URL if no new file selected
        moduleData.videoUrl = uploadedVideoUrl || selectedModule.videoUrl;
      }
      
      if (moduleForm.type === 'interactive') {
        const validTexts = interactiveTexts.filter(text => text.trim() !== '');
        moduleData.interactiveContent = {
          texts: validTexts,
          currentIndex: 0
        };
      } else if (moduleForm.type === 'quiz') {
        const validQuestions = quizQuestions.filter(q => 
          q.question.trim() !== '' && 
          q.options.every(option => option.trim() !== '')
        );
        moduleData.quizContent = {
          questions: validQuestions,
          passingScore,
          allowRetry
        };
      }

      await saveCourseModules(replaceModule(editingCourse, { ...selectedModule, ...moduleData } as CourseModule));

      toast.success(t('dashboard.instructor.dashboard.success.moduleUpdated'));
      
      // Reset form and selection
      setSelectedModule(null);
      setModuleForm({
        id: uuidv4(),
        title: '',
        duration: '',
        type: 'video',
        content: ''
      });
      setInteractiveTexts(['']);
      clearVideoSelection();

      // Refresh courses to get updated data
      await loadCourses();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error(t('dashboard.instructor.errors.updateCourse'));
    }
  };

  if (loading) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              {t('dashboard.instructor.dashboard.courseManagement')}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            {t('dashboard.instructor.dashboard.courseManagement')}
          </CardTitle>
          <Dialog open={isAddingCourse} onOpenChange={setIsAddingCourse}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard.instructor.dashboard.addCourse')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingCourse ? t('dashboard.instructor.dashboard.editCourse') : t('dashboard.instructor.dashboard.addNewCourse')}
                </DialogTitle>
                <DialogDescription>
                  {t('dashboard.instructor.dashboard.courseFormDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('dashboard.instructor.dashboard.courseTitle')}</label>
                  <Input
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    placeholder={t('dashboard.instructor.dashboard.enterCourseTitle')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('dashboard.instructor.dashboard.courseDescription')}</label>
                  <Textarea
                    value={newCourse.description}
                    onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                    placeholder={t('dashboard.instructor.dashboard.enterCourseDescription')}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('dashboard.instructor.dashboard.category')}</label>
                  <Select
                    value={newCourse.category}
                    onValueChange={(value) => setNewCourse({ ...newCourse, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dashboard.instructor.dashboard.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nutrition">{t('dashboard.instructor.dashboard.categories.nutrition')}</SelectItem>
                      <SelectItem value="mentalHealth">{t('dashboard.instructor.dashboard.categories.mentalHealth')}</SelectItem>
                      <SelectItem value="fitness">{t('dashboard.instructor.dashboard.categories.fitness')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('dashboard.instructor.dashboard.totalLessons')}</label>
                  <Input
                    type="number"
                    value={newCourse.totalLessons}
                    onChange={(e) => setNewCourse({ ...newCourse, totalLessons: parseInt(e.target.value) })}
                    placeholder={t('dashboard.instructor.dashboard.enterTotalLessons')}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newCourse.certificate}
                    onChange={(e) => setNewCourse({ ...newCourse, certificate: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label className="text-sm font-medium">{t('dashboard.instructor.dashboard.offerCertificate')}</label>
                </div>
                <Button 
                  onClick={editingCourse ? handleUpdateCourse : handleAddCourse} 
                  className="w-full"
                >
                  {editingCourse ? t('dashboard.instructor.dashboard.updateCourse') : t('dashboard.instructor.dashboard.addCourse')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {courses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex flex-col gap-4 p-4 border rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{course.title}</span>
                    {course.certificate && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {t('dashboard.instructor.dashboard.certificateAvailable')}
                      </span>
                    )}

                  </div>
                  <p className="text-sm text-muted-foreground">{course.description}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {t(`dashboard.instructor.dashboard.categories.${course.category}`)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {course.totalLessons} {t('dashboard.instructor.dashboard.lessons')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleManageModules(course)}
                  >
                    <List className="h-4 w-4 mr-2" />
                    {t('dashboard.instructor.dashboard.manageModules')}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditCourse(course)}
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    {t('dashboard.instructor.dashboard.edit')}
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteCourse(course.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('dashboard.instructor.dashboard.delete')}
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </CardContent>

      {/* Module Management Dialog */}
      <Dialog open={isManagingModules} onOpenChange={setIsManagingModules}>
        <DialogContent className="max-w-[90vw] h-[90vh] p-0">
          <DialogHeader className="p-6 border-b">
            <DialogTitle className="text-2xl font-bold">
              {t('dashboard.instructor.dashboard.manageModules')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.instructor.dashboard.moduleList')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex h-[calc(90vh-8rem)]">
            {/* Sidebar */}
            <div className="w-[28rem] border-l bg-muted flex flex-col">
              <div className="p-4 border-b bg-muted/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">
                    {t('dashboard.instructor.dashboard.modules')}
                  </h3>
                  <Badge variant="secondary">
                    {newCourse.modules?.length || 0} {t('dashboard.instructor.dashboard.modules')}
                  </Badge>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {(newCourse.modules || []).map((module, moduleIndex) => (
                  <div
                    key={module.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedModule?.id === module.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-accent'
                    }`}
                    onClick={() => handleSelectModule(module)}
                  >
                    <div className="flex items-center justify-between">
                      {/* Far Left: Movement Controls */}
                      <div className="flex items-center gap-2">
                        {selectedModule?.id === module.id && (
                          <div className="flex flex-col">
                            {/* Move Up Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveModuleUp(moduleIndex);
                              }}
                              disabled={moduleIndex === 0}
                              title={t('dashboard.instructor.dashboard.moveUp')}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            
                            {/* Move Down Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveModuleDown(moduleIndex);
                              }}
                              disabled={moduleIndex === (editingCourse?.modules?.length || 0) - 1}
                              title={t('dashboard.instructor.dashboard.moveDown')}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Center: Module Content */}
                      <div className="flex-1 px-4">
                        <h4 className="font-medium">{module.title}</h4>
                      </div>

                      {/* Far Right: Action Controls */}
                      <div className="flex items-center gap-2">
                        {selectedModule?.id === module.id && (
                          <div className="flex flex-col">
                            {/* Duplicate Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateModule(moduleIndex);
                              }}
                              title={t('dashboard.instructor.dashboard.duplicate')}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            
                            {/* Delete Button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary/90"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteModule(moduleIndex);
                              }}
                              title={t('dashboard.instructor.dashboard.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                     </div>
                    <p className="text-sm opacity-80">
                      {module.duration} {t('dashboard.instructor.dashboard.moduleDuration')}
                    </p>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t">
                <Button
                  className="w-full"
                  onClick={handleAddModule}
                >
                  <PlusCircle className="h-4 w-4 ml-2" />
                  {t('dashboard.instructor.dashboard.addNewModule')}
                </Button>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                {selectedModule ? (
                  <>
                    <div className="mb-6 flex items-center justify-between">
                      <div>
                  <h3 className="text-lg font-semibold mb-2">
                          {t('dashboard.instructor.dashboard.editModule')}
                  </h3>
                  <p className="text-muted-foreground">
                          {t('dashboard.instructor.dashboard.editModuleDescription')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setSelectedModule(null);
                          setModuleForm({
                            id: uuidv4(),
                            title: '',
                            duration: '',
                            type: 'video',
                            content: ''
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                </div>

                <form onSubmit={handleModuleSubmit} className="space-y-6">
                  <div className="grid gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        {t('dashboard.instructor.dashboard.moduleTitle')}
                      </Label>
                      <Input
                        id="title"
                        value={moduleForm.title}
                        onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                        placeholder={t('dashboard.instructor.dashboard.enterModuleTitle')}
                        className="h-10"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="duration">
                          {t('dashboard.instructor.dashboard.moduleDuration')}
                        </Label>
                        <Input
                          id="duration"
                          type="number"
                          value={moduleForm.duration}
                          onChange={(e) => setModuleForm({ ...moduleForm, duration: e.target.value })}
                          placeholder={t('dashboard.instructor.dashboard.enterModuleDuration')}
                          className="h-10"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="type">
                          {t('dashboard.instructor.dashboard.moduleType')}
                        </Label>
                        <Select
                          value={moduleForm.type}
                          onValueChange={(value: 'video' | 'reading' | 'quiz' | 'interactive') => {
                            setModuleForm({ ...moduleForm, type: value });
                            if (value === 'interactive') {
                              setInteractiveTexts(['']);
                            } else if (value === 'quiz') {
                              setQuizQuestions([{
                                id: uuidv4(),
                                question: '',
                                options: ['', '', '', ''],
                                correctAnswer: 0,
                                explanation: ''
                              }]);
                              setPassingScore(70);
                              setAllowRetry(true);
                            }
                          }}
                        >
                          <SelectTrigger className="h-10">
                                <SelectValue placeholder={t('dashboard.instructor.dashboard.selectModuleType')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="video">{t('dashboard.instructor.dashboard.moduleTypes.video')}</SelectItem>
                            <SelectItem value="reading">{t('dashboard.instructor.dashboard.moduleTypes.reading')}</SelectItem>
                            <SelectItem value="quiz">{t('dashboard.instructor.dashboard.moduleTypes.quiz')}</SelectItem>
                            <SelectItem value="interactive">{t('dashboard.instructor.dashboard.moduleTypes.interactive')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {moduleForm.type === 'video' && (
                      <div className="space-y-4">
                        <Label>{t('dashboard.instructor.dashboard.videoUpload')}</Label>
                        
                        {/* Video Preview */}
                        {uploadedVideoUrl && (
                          <div className="relative rounded-lg overflow-hidden bg-black/5">
                            <video 
                              src={uploadedVideoUrl} 
                              controls 
                              className="w-full max-h-[200px] object-contain"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={clearVideoSelection}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Upload Progress */}
                        {isUploadingVideo && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">{t('dashboard.instructor.dashboard.uploadingVideo')}...</span>
                            </div>
                            <Progress value={videoUploadProgress} className="h-2" />
                            <span className="text-xs text-muted-foreground">{Math.round(videoUploadProgress)}%</span>
                          </div>
                        )}
                        
                        {/* Upload Button */}
                        {!uploadedVideoUrl && !isUploadingVideo && (
                          <div 
                            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                            onClick={() => videoInputRef.current?.click()}
                          >
                            <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                            <p className="text-sm font-medium">{t('dashboard.instructor.dashboard.clickToUploadVideo')}</p>
                            <p className="text-xs text-muted-foreground mt-1">{t('dashboard.instructor.dashboard.videoMaxSize')}</p>
                          </div>
                        )}
                        
                        {/* Change Video Button */}
                        {uploadedVideoUrl && !isUploadingVideo && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => videoInputRef.current?.click()}
                            className="w-full"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {t('dashboard.instructor.dashboard.changeVideo')}
                          </Button>
                        )}
                        
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          onChange={handleVideoFileSelect}
                          className="hidden"
                        />
                      </div>
                    )}

                    {moduleForm.type === 'reading' && (
                    <div className="space-y-2">
                      <Label htmlFor="content">{t('dashboard.instructor.dashboard.moduleContent')}</Label>
                      <Textarea
                        id="content"
                        value={moduleForm.content || ''}
                        onChange={(e) => setModuleForm({ ...moduleForm, content: e.target.value })}
                        placeholder={t('dashboard.instructor.dashboard.enterModuleContent')}
                        className="min-h-[150px]"
                      />
                    </div>
                    )}

                    {moduleForm.type === 'interactive' && (
                      <div className="space-y-4">
                        <Label>{t('dashboard.instructor.dashboard.interactiveTexts')}</Label>
                        <div className="space-y-3">
                          {interactiveTexts.map((text, index) => (
                            <div key={index} className="flex gap-2">
                              <Textarea
                                value={text}
                                onChange={(e) => {
                                  const newTexts = [...interactiveTexts];
                                  newTexts[index] = e.target.value;
                                  setInteractiveTexts(newTexts);
                                }}
                                placeholder={t('dashboard.instructor.dashboard.textPlaceholder').replace('{number}', (index + 1).toString())}
                                className="min-h-[80px] resize-none"
                                maxLength={280}
                              />
                              <div className="flex flex-col gap-1">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (interactiveTexts.length > 1) {
                                      const newTexts = interactiveTexts.filter((_, i) => i !== index);
                                      setInteractiveTexts(newTexts);
                                    }
                                  }}
                                  disabled={interactiveTexts.length === 1}
                                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                {index === interactiveTexts.length - 1 && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setInteractiveTexts([...interactiveTexts, ''])}
                                    className="border-green-200 text-green-600 hover:bg-green-50 hover:text-green-700 hover:border-green-300"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          <div className="text-xs text-muted-foreground">
                            {t('dashboard.instructor.dashboard.interactiveDescription')}
                          </div>
                        </div>
                      </div>
                    )}

                    {moduleForm.type === 'quiz' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{t('dashboard.instructor.dashboard.passingScore')}</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={passingScore}
                              onChange={(e) => setPassingScore(Number(e.target.value))}
                              placeholder="70"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={allowRetry}
                                onChange={(e) => setAllowRetry(e.target.checked)}
                                className="h-4 w-4"
                              />
                              {t('dashboard.instructor.dashboard.allowRetry')}
                            </Label>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <Label>{t('dashboard.instructor.dashboard.quizQuestions')}</Label>
                          {quizQuestions.map((question, qIndex) => (
                            <div key={question.id} className="p-4 border rounded-lg space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="font-medium">{t('dashboard.instructor.dashboard.question')} {qIndex + 1}</Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (quizQuestions.length > 1) {
                                      setQuizQuestions(quizQuestions.filter((_, i) => i !== qIndex));
                                    }
                                  }}
                                  disabled={quizQuestions.length === 1}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              <Textarea
                                value={question.question}
                                onChange={(e) => {
                                  const newQuestions = [...quizQuestions];
                                  newQuestions[qIndex].question = e.target.value;
                                  setQuizQuestions(newQuestions);
                                }}
                                placeholder={t('dashboard.instructor.dashboard.questionPlaceholder')}
                                className="min-h-[80px]"
                              />
                              
                              <div className="space-y-2">
                                <Label className="text-sm">{t('dashboard.instructor.dashboard.answerOptions')}</Label>
                                {question.options.map((option, oIndex) => (
                                  <div key={oIndex} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`correct-${question.id}`}
                                      checked={question.correctAnswer === oIndex}
                                      onChange={() => {
                                        const newQuestions = [...quizQuestions];
                                        newQuestions[qIndex].correctAnswer = oIndex;
                                        setQuizQuestions(newQuestions);
                                      }}
                                      className="h-4 w-4"
                                    />
                                    <Input
                                      value={option}
                                      onChange={(e) => {
                                        const newQuestions = [...quizQuestions];
                                        newQuestions[qIndex].options[oIndex] = e.target.value;
                                        setQuizQuestions(newQuestions);
                                      }}
                                      placeholder={`${t('dashboard.instructor.dashboard.option')} ${oIndex + 1}`}
                                      className="flex-1"
                                    />
                                  </div>
                                ))}
                              </div>
                              
                              <div className="space-y-2">
                                <Label className="text-sm">{t('dashboard.instructor.dashboard.explanation')} ({t('dashboard.instructor.dashboard.optional')})</Label>
                                <Textarea
                                  value={question.explanation || ''}
                                  onChange={(e) => {
                                    const newQuestions = [...quizQuestions];
                                    newQuestions[qIndex].explanation = e.target.value;
                                    setQuizQuestions(newQuestions);
                                  }}
                                  placeholder={t('dashboard.instructor.dashboard.explanationPlaceholder')}
                                  className="min-h-[60px]"
                                />
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setQuizQuestions([...quizQuestions, {
                              id: uuidv4(),
                              question: '',
                              options: ['', '', '', ''],
                              correctAnswer: 0,
                              explanation: ''
                            }])}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {t('dashboard.instructor.dashboard.addQuestion')}
                          </Button>
                          
                          <div className="text-xs text-muted-foreground">
                            {t('dashboard.instructor.dashboard.quizDescription')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedModule(null);
                        setModuleForm({
                              id: uuidv4(),
                          title: '',
                          duration: '',
                              type: 'video',
                              content: ''
                        });
                        setInteractiveTexts(['']);
                        setQuizQuestions([{
                          id: uuidv4(),
                          question: '',
                          options: ['', '', '', ''],
                          correctAnswer: 0,
                          explanation: ''
                        }]);
                        setPassingScore(70);
                        setAllowRetry(true);
                      }}
                    >
                      {t('dashboard.instructor.dashboard.cancel')}
                    </Button>
                        <Button type="submit">
                          {t('dashboard.instructor.dashboard.saveChanges')}
                      </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <h3 className="text-lg font-semibold mb-2">
                      {t('dashboard.instructor.dashboard.selectOrAddModule')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dashboard.instructor.dashboard.selectOrAddModuleDescription')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Module Dialog */}
      <Dialog open={isAddingModule} onOpenChange={setIsAddingModule}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="flex-shrink-0 px-6 pt-6">
            <DialogTitle>
              {t('dashboard.instructor.dashboard.addNewModule')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.instructor.dashboard.addModuleDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-6 py-4">
            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="new-title">
                  {t('dashboard.instructor.dashboard.moduleTitle')}
                </Label>
                <Input
                  id="new-title"
                  value={newModule.title}
                  onChange={(e) => setNewModule({ ...newModule, title: e.target.value })}
                  placeholder={t('dashboard.instructor.dashboard.enterModuleTitle')}
                  className="h-10"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-duration">
                    {t('dashboard.instructor.dashboard.moduleDuration')}
                  </Label>
                  <Input
                    id="new-duration"
                    type="number"
                    value={newModule.duration}
                    onChange={(e) => setNewModule({ ...newModule, duration: e.target.value })}
                    placeholder={t('dashboard.instructor.dashboard.enterModuleDuration')}
                    className="h-10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-type">
                    {t('dashboard.instructor.dashboard.moduleType')}
                  </Label>
                  <Select
                    value={newModule.type}
                    onValueChange={(value: 'video' | 'reading' | 'quiz' | 'interactive') => {
                      setNewModule({ ...newModule, type: value });
                      if (value === 'interactive') {
                        setInteractiveTexts(['']);
                      } else if (value === 'quiz') {
                        setQuizQuestions([{
                          id: uuidv4(),
                          question: '',
                          options: ['', '', '', ''],
                          correctAnswer: 0,
                          explanation: ''
                        }]);
                        setPassingScore(70);
                        setAllowRetry(true);
                      }
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder={t('dashboard.instructor.dashboard.selectModuleType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">{t('dashboard.instructor.dashboard.moduleTypes.video')}</SelectItem>
                      <SelectItem value="reading">{t('dashboard.instructor.dashboard.moduleTypes.reading')}</SelectItem>
                      <SelectItem value="quiz">{t('dashboard.instructor.dashboard.moduleTypes.quiz')}</SelectItem>
                      <SelectItem value="interactive">{t('dashboard.instructor.dashboard.moduleTypes.interactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newModule.type === 'video' && (
                <div className="space-y-4">
                  <Label>{t('dashboard.instructor.dashboard.videoUpload')}</Label>
                  
                  {/* Video Preview */}
                  {uploadedVideoUrl && (
                    <div className="relative rounded-lg overflow-hidden bg-black/5">
                      <video 
                        src={uploadedVideoUrl} 
                        controls 
                        className="w-full max-h-[200px] object-contain"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={clearVideoSelection}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Upload Progress */}
                  {isUploadingVideo && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">{t('dashboard.instructor.dashboard.uploadingVideo')}...</span>
                      </div>
                      <Progress value={videoUploadProgress} className="h-2" />
                      <span className="text-xs text-muted-foreground">{Math.round(videoUploadProgress)}%</span>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {!uploadedVideoUrl && !isUploadingVideo && (
                    <div 
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-sm font-medium">{t('dashboard.instructor.dashboard.clickToUploadVideo')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('dashboard.instructor.dashboard.videoMaxSize')}</p>
                    </div>
                  )}
                  
                  {/* Change Video Button */}
                  {uploadedVideoUrl && !isUploadingVideo && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => videoInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('dashboard.instructor.dashboard.changeVideo')}
                    </Button>
                  )}
                  
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoFileSelect}
                    className="hidden"
                  />
                </div>
              )}

              {newModule.type === 'reading' && (
              <div className="space-y-2">
                <Label htmlFor="new-content">{t('dashboard.instructor.dashboard.moduleContent')}</Label>
                <Textarea
                  id="new-content"
                  value={newModule.content || ''}
                  onChange={(e) => setNewModule({ ...newModule, content: e.target.value })}
                  placeholder={t('dashboard.instructor.dashboard.enterModuleContent')}
                  className="min-h-[150px]"
                />
              </div>
              )}

              {newModule.type === 'interactive' && (
                <div className="space-y-4">
                  <Label>{t('dashboard.instructor.dashboard.interactiveTexts')}</Label>
                  <div className="space-y-3">
                    {interactiveTexts.map((text, index) => (
                      <div key={index} className="flex gap-2">
                        <Textarea
                          value={text}
                          onChange={(e) => {
                            const newTexts = [...interactiveTexts];
                            newTexts[index] = e.target.value;
                            setInteractiveTexts(newTexts);
                          }}
                          placeholder={t('dashboard.instructor.dashboard.textPlaceholder').replace('{number}', (index + 1).toString())}
                          className="min-h-[80px] resize-none"
                          maxLength={280}
                        />
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (interactiveTexts.length > 1) {
                                const newTexts = interactiveTexts.filter((_, i) => i !== index);
                                setInteractiveTexts(newTexts);
                              }
                            }}
                            disabled={interactiveTexts.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          {index === interactiveTexts.length - 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setInteractiveTexts([...interactiveTexts, ''])}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
            </div>
                      </div>
                    ))}
                    <div className="text-xs text-muted-foreground">
                      {t('dashboard.instructor.dashboard.interactiveDescription')}
                    </div>
                  </div>
                </div>
              )}

              {newModule.type === 'quiz' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('dashboard.instructor.dashboard.passingScore')}</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={passingScore}
                        onChange={(e) => setPassingScore(Number(e.target.value))}
                        placeholder="70"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={allowRetry}
                          onChange={(e) => setAllowRetry(e.target.checked)}
                          className="h-4 w-4"
                        />
                        {t('dashboard.instructor.dashboard.allowRetry')}
                      </Label>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Label>{t('dashboard.instructor.dashboard.quizQuestions')}</Label>
                    {quizQuestions.map((question, qIndex) => (
                      <div key={question.id} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-medium">{t('dashboard.instructor.dashboard.question')} {qIndex + 1}</Label>
              <Button
                type="button"
                variant="outline"
                            size="sm"
                            onClick={() => {
                              if (quizQuestions.length > 1) {
                                setQuizQuestions(quizQuestions.filter((_, i) => i !== qIndex));
                              }
                            }}
                            disabled={quizQuestions.length === 1}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <Textarea
                          value={question.question}
                          onChange={(e) => {
                            const newQuestions = [...quizQuestions];
                            newQuestions[qIndex].question = e.target.value;
                            setQuizQuestions(newQuestions);
                          }}
                          placeholder={t('dashboard.instructor.dashboard.questionPlaceholder')}
                          className="min-h-[80px]"
                        />
                        
                        <div className="space-y-2">
                          <Label className="text-sm">{t('dashboard.instructor.dashboard.answerOptions')}</Label>
                          {question.options.map((option, oIndex) => (
                            <div key={oIndex} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`new-correct-${question.id}`}
                                checked={question.correctAnswer === oIndex}
                                onChange={() => {
                                  const newQuestions = [...quizQuestions];
                                  newQuestions[qIndex].correctAnswer = oIndex;
                                  setQuizQuestions(newQuestions);
                                }}
                                className="h-4 w-4"
                              />
                              <Input
                                value={option}
                                onChange={(e) => {
                                  const newQuestions = [...quizQuestions];
                                  newQuestions[qIndex].options[oIndex] = e.target.value;
                                  setQuizQuestions(newQuestions);
                                }}
                                placeholder={`${t('dashboard.instructor.dashboard.option')} ${oIndex + 1}`}
                                className="flex-1"
                              />
                            </div>
                          ))}
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-sm">{t('dashboard.instructor.dashboard.explanation')} ({t('dashboard.instructor.dashboard.optional')})</Label>
                          <Textarea
                            value={question.explanation || ''}
                            onChange={(e) => {
                              const newQuestions = [...quizQuestions];
                              newQuestions[qIndex].explanation = e.target.value;
                              setQuizQuestions(newQuestions);
                            }}
                            placeholder={t('dashboard.instructor.dashboard.explanationPlaceholder')}
                            className="min-h-[60px]"
                          />
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setQuizQuestions([...quizQuestions, {
                        id: uuidv4(),
                        question: '',
                        options: ['', '', '', ''],
                        correctAnswer: 0,
                        explanation: ''
                      }])}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('dashboard.instructor.dashboard.addQuestion')}
                    </Button>
                    
                    <div className="text-xs text-muted-foreground">
                      {t('dashboard.instructor.dashboard.quizDescription')}
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 px-6 pb-6 border-t flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsAddingModule(false);
                setInteractiveTexts(['']);
                setQuizQuestions([{
                  id: uuidv4(),
                  question: '',
                  options: ['', '', '', ''],
                  correctAnswer: 0,
                  explanation: ''
                }]);
                setPassingScore(70);
                setAllowRetry(true);
                clearVideoSelection();
              }}
              >
                {t('dashboard.instructor.dashboard.cancel')}
              </Button>
              <Button type="button" onClick={handleSaveModule} disabled={isUploadingVideo}>
                {isUploadingVideo ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('dashboard.instructor.dashboard.uploadingVideo')}...
                  </>
                ) : (
                  t('dashboard.instructor.dashboard.saveChanges')
                )}
              </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 